/**
 * extractEmbeddedJson — pull a JSON / Python-dict blob out of a free-text
 * string (typically a workflow error message) so it can be rendered with the
 * same `FormattedJsonView` the Results section uses.
 *
 * Substrate errors frequently splice a Python `repr()` of a result dict into
 * the message, e.g.:
 *
 *   RuntimeError: close_pchg_for_client: update_activity returned
 *   success=False sdk_id=39510797 result={'success': False, 'code':
 *   'EPIC_WRITES_GUARDED', 'message': '…', 'details': {…}}
 *
 * That blob is *almost* JSON — single quotes, and Python's `True`/`False`/
 * `None` literals. This helper finds the first balanced `{…}` / `[…]` blob,
 * normalises Python literals to JSON, and parses it. The text before/after the
 * blob is returned verbatim so the caller can render a "prefix + formatted
 * JSON + suffix" layout.
 *
 * Returns `null` when there is no parseable embedded object — callers fall back
 * to rendering the raw string unchanged.
 */

export interface ExtractedJson {
  /** Text before the JSON blob (e.g. "RuntimeError: … result="). */
  prefix: string;
  /** The parsed object/array. */
  data: unknown;
  /** Any text after the blob (usually empty). */
  suffix: string;
}

/** Index of the first `{` or `[`, or -1 if neither is present. */
function firstBracketIndex(s: string): number {
  const a = s.indexOf("{");
  const b = s.indexOf("[");
  if (a === -1) return b;
  if (b === -1) return a;
  return Math.min(a, b);
}

/**
 * Return the balanced bracket substring starting at `start` (inclusive of the
 * opening and matching closing bracket), or null when unbalanced. String
 * literals (single OR double quoted, with backslash escapes) are skipped so
 * brackets inside strings don't throw off the depth count.
 */
function extractBalanced(s: string, start: number): string | null {
  let depth = 0;
  let inStr: string | null = null;
  let escaped = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      if (escaped) escaped = false;
      else if (c === "\\") escaped = true;
      else if (c === inStr) inStr = null;
      continue;
    }
    if (c === "'" || c === '"') {
      inStr = c;
      continue;
    }
    if (c === "{" || c === "[") depth++;
    else if (c === "}" || c === "]") {
      depth--;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return null;
}

/**
 * Convert a Python-literal blob to JSON text: single-quoted strings become
 * double-quoted (escaping any interior `"`), and the bare `True` / `False` /
 * `None` tokens become `true` / `false` / `null`. Double-quoted strings and
 * everything else are copied through untouched. Operates char-by-char so
 * apostrophes inside strings (e.g. "don't") are preserved.
 */
function pythonLiteralToJson(s: string): string {
  let out = "";
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    // Pass a double-quoted string through verbatim (already JSON-shaped).
    if (c === '"') {
      out += c;
      i++;
      while (i < s.length) {
        const d = s[i];
        if (d === "\\") {
          out += d + (s[i + 1] ?? "");
          i += 2;
          continue;
        }
        out += d;
        i++;
        if (d === '"') break;
      }
      continue;
    }
    // Convert a single-quoted string to a JSON double-quoted string.
    if (c === "'") {
      i++; // skip opening quote
      let str = "";
      while (i < s.length) {
        const d = s[i];
        if (d === "\\") {
          const e = s[i + 1] ?? "";
          str += e === "'" ? "'" : "\\" + e; // \' → ' ; keep other escapes
          i += 2;
          continue;
        }
        if (d === "'") {
          i++;
          break;
        }
        if (d === '"') {
          str += '\\"'; // escape interior double quote
          i++;
          continue;
        }
        str += d;
        i++;
      }
      out += '"' + str + '"';
      continue;
    }
    // Bare identifiers — rewrite Python literals, leave anything else.
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < s.length && /[A-Za-z0-9_]/.test(s[j]!)) j++;
      const word = s.slice(i, j);
      out += word === "True" ? "true" : word === "False" ? "false" : word === "None" ? "null" : word;
      i = j;
      continue;
    }
    out += c;
    i++;
  }
  return out;
}

function tryParse(blob: string): unknown {
  // Already-valid JSON first (cheapest, and avoids mangling).
  try {
    return JSON.parse(blob);
  } catch {
    /* fall through to Python normalisation */
  }
  try {
    return JSON.parse(pythonLiteralToJson(blob));
  } catch {
    return undefined;
  }
}

export function extractEmbeddedJson(text: string | null | undefined): ExtractedJson | null {
  if (typeof text !== "string" || text.length === 0) return null;
  const start = firstBracketIndex(text);
  if (start === -1) return null;
  const blob = extractBalanced(text, start);
  if (!blob) return null;
  const data = tryParse(blob);
  // Only treat it as "embedded JSON" when the blob parsed to a container —
  // a stray `{` in prose shouldn't trigger the formatted view.
  if (data === undefined || data === null || typeof data !== "object") return null;
  return {
    prefix: text.slice(0, start).trimEnd(),
    data,
    suffix: text.slice(start + blob.length).trimStart(),
  };
}

export default extractEmbeddedJson;
